import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Typography, CircularProgress, Button,
  TextField, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DownloadIcon from "@mui/icons-material/Download";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { axiosConfig } from "../../axios/axiosConfig";
import PaginationControls from "../Pagination/PaginationControls";

const PAGE_SIZE = 50;
const FIXED_KEYS = ["date", "user_id", "username", "phoneno"];
const LABEL_MAP = {
  Trade_Calls_Screen: "Premium Research Call",
  Detail_page_Screen: "Stock Detail Page",
  Trade_Advisory_Screen: "Mid/ Long Term Trade",
  Trade_Performance_Screen: "Screener Performances",
  thankyou_event: "Subscription Event",
  thankyou_event_fail: "Subscription Failed Event",
  thankyou_event_topup: "Subscription Event Topup",
  thankyou_event_topup_fail: "Subscription Event Topup Failed",
  Alert_Screen: "Signals",
};
const fmtLabel = (key) => LABEL_MAP[key] || key;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-") : "—";

const EventLogUserDetails = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (appliedFrom) params.append("from_date", appliedFrom);
        if (appliedTo) params.append("to_date", appliedTo);
        params.append("page", page);
        const res = await axiosConfig({ method: "get", url: `/investors/event-log-user-details/?${params}` });
        const raw = res?.data || {};
        setTotalCount(raw.count || 0);
        const results = raw.results || [];
        setData(results);

        if (results.length > 0) {
          const cols = Object.keys(results[0])
            .filter((k) => !FIXED_KEYS.includes(k))
            .map((k) => ({ key: k, label: fmtLabel(k) }));
          setDynamicColumns(cols);
        }
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [appliedFrom, appliedTo, page]);

  const handleSearch = () => {
    setPage(1);
    setDynamicColumns([]);
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      const dlFrom = fromDate || appliedFrom;
      const dlTo = toDate || appliedTo;
      if (dlFrom) params.append("from_date", dlFrom);
      if (dlTo) params.append("to_date", dlTo);
      params.append("download", "true");
      const res = await axiosConfig({
        method: "get",
        url: `/investors/event-log-user-details/?${params}`,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "event_log_user_details.xlsx".toLowerCase());
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error("download error:", e);
    } finally {
      setDownloading(false);
    }
  };

  const colSpan = 5 + dynamicColumns.length;

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box onClick={() => navigate("/investor")} sx={{ width: 34, height: 34, borderRadius: "8px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "#f1f5f9" } }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </Box>
          <Box sx={{ width: 38, height: 38, borderRadius: "10px", background: "#04343a", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EventAvailableIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>Event Log — User Details</Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Per-user screen visit events</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            type="date" size="small" label="From" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }}
          />
          <TextField
            type="date" size="small" label="To" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }}
          />
          <Button
            variant="contained" size="small"
            startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />}
            onClick={handleSearch}
            sx={{ height: 36, borderRadius: "8px", background: "#04343a", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, "&:hover": { background: "#016551" } }}
          >
            Filter
          </Button>
          <Button
            variant="contained" size="small"
            startIcon={downloading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleDownload} disabled={downloading}
            sx={{ height: 36, borderRadius: "8px", background: "#016551", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, boxShadow: "0 4px 12px rgba(1,101,81,0.3)", "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" } }}
          >
            {downloading ? "Downloading…" : "Download"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error" sx={{ borderRadius: "10px" }}>{error}</Alert>}

        <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.8, background: "#04343a", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>USER EVENT LOG</Typography>
            {!loading && <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{totalCount.toLocaleString("en-IN")} records</Typography>}
          </Box>

          <TableContainer sx={{ overflowX: "auto", maxHeight: "calc(100vh - 200px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>#</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>Date</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>User ID</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>Username</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>Phone</TableCell>
                  {dynamicColumns.map((col) => (
                    <TableCell key={col.key} align="center" sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={colSpan} sx={{ textAlign: "center", py: 6 }}><CircularProgress size={28} sx={{ color: "#04343a" }} /></TableCell></TableRow>
                ) : data.length === 0 ? (
                  <TableRow><TableCell colSpan={colSpan} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>No data found</TableCell></TableRow>
                ) : (
                  data.map((row, idx) => (
                    <TableRow key={`${row.user_id}-${row.date}-${idx}`} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(row.date)}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{row.user_id}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>{row.username}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#475569" }}>{row.phoneno}</TableCell>
                      {dynamicColumns.map((col) => (
                        <TableCell key={col.key} align="center">
                          <Chip
                            label={row[col.key] ?? "—"}
                            size="small"
                            sx={{
                              fontSize: 10, fontWeight: 700, height: 20, minWidth: 32,
                              background: row[col.key] === 1 ? "#dcfce7" : "#f1f5f9",
                              color: row[col.key] === 1 ? "#16a34a" : "#94a3b8",
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && totalCount > 0 && (
            <PaginationControls totalCount={totalCount} page={page} setPage={setPage} offset={PAGE_SIZE} />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default EventLogUserDetails;
